import { EntityState } from "@ngrx/entity";
import { IPersonAccess } from "./entities/access/access.interface";
import { IPersonAddress } from "./entities/address/address.interface";
import { IPersonCertificate } from "./entities/certificate/certificate.interface";
import { IPersonComment } from "./entities/comment/comment.interface";
import { IPersonGroup } from "./entities/group/group.interface";
import { IPersonIntegration } from "./entities/integration/integration.interface";
import { IPerson, IPersonPhone } from "./entities/person.interface";
import { IPersonPreferences } from "./entities/preferences/preferences.interface";
import { IPersonResponsible } from "./entities/responsible/responsible.interface";
import { IPersonTax } from "./entities/tax/tax.interface";
import { patchState, signalStore, type, withMethods, withState } from "@ngrx/signals";
import { addEntities, removeEntity, withEntities, updateEntity, addEntity, entityConfig } from '@ngrx/signals/entities';
import { inject } from "@angular/core";
import { tapResponse } from "@ngrx/operators";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, tap, switchMap } from "rxjs";
import { PersonService } from "./infra/person.service";
import { withReqState, setPropError, setPropLoading, setPropLoaded, setPropInit } from "@application/data-access";
import { HttpErrorResponse } from "@angular/common/http";
import { ErrorStore } from '@application/errors/data-access';
import { AlertStore } from "@alert/data-access";

export interface IPersonStore {
    personData: IPerson;
    currentPersonId: string;
    loadPersonError: HttpErrorResponse,
    personAddresses: EntityState<IPersonAddress>;
    personPhones: EntityState<IPersonPhone>;
    personAccess: IPersonAccess;
    personPreferences: IPersonPreferences;
    personResponsible: IPersonResponsible;
    personIntegration: IPersonIntegration;
    personCertificate: IPersonCertificate;
    personComment: IPersonComment;
    personGroup: EntityState<IPersonGroup>;
    personTax: IPersonTax;
}

export const initialPersonStore: IPersonStore = {
    personData: null,
    currentPersonId: null,
    personAddresses: null,
    loadPersonError: null,
    personPhones: null,
    personAccess: null,
    personPreferences: null,
    personResponsible: null,
    personIntegration: null,
    personCertificate: null,
    personComment: null,
    personGroup: null,
    personTax: null,
};


const personPhoneConfig = entityConfig({
  entity: type<IPersonPhone>(),
  collection: 'personPhones',
  selectId: (phone: IPersonPhone) => phone.PhoneId
})

export const PhonesStore = signalStore(
    { providedIn: 'root' },
    withEntities(personPhoneConfig),
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore)
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonPhones', 'submitPersonPhones', 'deletePersonPhones')});
        },

        loadPersonPhones: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('getPersonPhones') })),
            switchMap((payload) => personService.getPersonPhones(payload)
                .pipe(
                    tapResponse({
                        next: (personPhones) => {
                            const phones = personPhones ?? [];
                            patchState(store, addEntities(phones, personPhoneConfig), setPropLoaded('getPersonPhones'))
                        },
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('getPersonPhones', error) })
                        }
                    })
                ))
        )),

        createPersonPhone: rxMethod<IPersonPhone>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonPhones') })),
            switchMap((payload) => personService.createPersonPhone(payload)
                .pipe(
                    tapResponse({
                        next: (personPhone) => {
                            patchState(store, addEntity(personPhone, personPhoneConfig), setPropLoaded('submitPersonPhones'))
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonPhones', error) })
                        }
                    })
                ))
        )),

        updatePersonPhone: rxMethod<IPersonPhone>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonPhones') })),
            switchMap((payload) => personService.updatePersonPhone(payload)
                .pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, updateEntity({ id: payload.PhoneId, changes: payload }, personPhoneConfig), setPropLoaded('submitPersonPhones'));
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonPhones', error) })
                        }
                    })
                ))
        )),

        deletePersonPhone: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('deletePersonPhones') })),
            switchMap((payload) => personService.deletePersonPhone(payload)
                .pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, removeEntity(payload, personPhoneConfig), setPropLoaded('deletePersonPhones'));
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('deletePersonPhones', error) })
                        }
                    })
                ))
        )),
    }),
    ),
    withReqState('getPersonPhones', 'deletePersonPhones','submitPersonPhones'),
)

export const AccessStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore)
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonAccess', 'submitPersonAccess')});
        },

        loadPersonAccess: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('getPersonAccess') })),
            switchMap((payload) => personService.getPersonAccess(payload)
                .pipe(
                    tapResponse({
                        next: (personAccess) => patchState(store, <any>{ personAccess, ...setPropLoaded('getPersonAccess') }),
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('getPersonAccess', error) })
                        }
                    })
                ))
        )),

        createPersonAccess: rxMethod<IPersonAccess>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonAccess') })),
            switchMap((payload) => personService.createPersonAccess(payload)
                .pipe(
                    tapResponse({
                        next: (personAccess) => {
                            patchState(store, <NonNullable<object>>{ personAccess, ...setPropLoaded('submitPersonAccess') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonAccess', error) })
                        }
                    })
                ))
        )),

        updatePersonAccess: rxMethod<IPersonAccess>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonAccess') })),
            switchMap((payload) => personService.updatePersonAccess(payload)
                .pipe(
                    tapResponse({
                        next: (personAccess) => {
                            patchState(store, <NonNullable<object>>{ personAccess, ...setPropLoaded('submitPersonAccess') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonAccess', error) })
                        }
                    })
                ))
        )),
    }
    )),
    withReqState('getPersonAccess'),
    withReqState('submitPersonAccess'),
)

export const PreferencesStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore)
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonPreferences', 'submitPersonPreferences')});
        },

        loadPersonPreferences: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('getPersonPreferences') })),
            switchMap((payload) => personService.getPersonPreferences(payload)
                .pipe(
                    tapResponse({
                        next: (personPreferences) => patchState(store, <NonNullable<object>>{ personPreferences, ...setPropLoaded('getPersonPreferences') }),
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('getPersonPreferences', error) })
                        }
                    })
                ))
        )),

        updatePersonPreferences: rxMethod<IPersonPreferences>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonPreferences') })),
            switchMap((payload) => personService.updatePersonPreferences(payload)
                .pipe(
                    tapResponse({
                        next: (personPreferences) => {
                            patchState(store, <NonNullable<object>>{ personPreferences, ...setPropLoaded('submitPersonPreferences') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonPreferences', error) })
                        }
                    })
                ))
        )),
    }
    )),
    withReqState('getPersonPreferences'),
    withReqState('submitPersonPreferences')

)

export const ResponsibleStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore)
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonResponsible', 'submitPersonResponsible')});
        },

        loadPersonResponsible: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('getPersonResponsible') })),
            switchMap((payload) => personService.getPersonResponsible(payload)
                .pipe(
                    tapResponse({
                        next: (personResponsible) => patchState(store, <NonNullable<object>>{ personResponsible, ...setPropLoaded('getPersonResponsible') }),
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('getPersonResponsible', error) })
                        }
                    })
                ))
        )),

        createPersonResponsible: rxMethod<IPersonResponsible>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonResponsible') })),
            switchMap((payload) => personService.createPersonResponsible(payload)
                .pipe(
                    tapResponse({
                        next: (personResponsible) => {
                            patchState(store, <NonNullable<object>>{ personResponsible, ...setPropLoaded('submitPersonResponsible') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonResponsible', error) })
                        }
                    })
                ))
        )),

        updatePersonResponsible: rxMethod<IPersonResponsible>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonResponsible') })),
            switchMap((payload) => personService.updatePersonResponsible(payload)
                .pipe(
                    tapResponse({
                        next: (personResponsible) => {
                            patchState(store, <NonNullable<object>>{ personResponsible, ...setPropLoaded('submitPersonResponsible') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonResponsible', error) })
                        }
                    })
                ))
        ))
    })
    ),
    withReqState('getPersonResponsible'),
    withReqState('submitPersonResponsible')
)

export const CertificateStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        alertStore = inject(AlertStore),
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonCertificate', 'submitPersonCertificate')});
        },
        loadPersonCertificate: rxMethod<string>(pipe(
            tap(() => patchState(store, { ...setPropLoading('getPersonCertificate') })),
            tap(() => {
                patchState(store, { ...setPropLoaded('getPersonCertificate') });
            }),
        )),
        createPersonCertificate: rxMethod<IPersonCertificate>(pipe(
            tap(() => patchState(store, { ...setPropLoading('submitPersonCertificate') })),
            tap(() => {
                patchState(store, { ...setPropLoaded('submitPersonCertificate') });
                alertStore.genericSuccess();
            }),
        )),
        updatePersonCertificate: rxMethod<IPersonCertificate>(pipe(
            tap(() => patchState(store, { ...setPropLoading('submitPersonCertificate') })),
            tap(() => {
                patchState(store, { ...setPropLoaded('submitPersonCertificate') });
                alertStore.genericSuccess();
            }),
        ))
    })
    ),
    withReqState('getPersonCertificate'),
    withReqState('submitPersonCertificate')
)

export const TaxStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore),
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonTax', 'submitPersonTax')});
        },
        loadPersonTax: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('getPersonTax') })),
            switchMap((payload) => personService.getPersonTax(payload)
                .pipe(
                    tapResponse({
                        next: (personTax) => patchState(store, <NonNullable<object>>{ personTax, ...setPropLoaded('getPersonTax') }),
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState(store, <NonNullable<object>>{ error, ...setPropError('getPersonTax', error) })
                        }
                    })
                ))
        )),
        updatePersonTax: rxMethod<IPersonTax>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonTax') })),
            switchMap((payload) => personService.updatePersonTax(payload)
                .pipe(
                    tapResponse({
                        next: (personTax) => {
                            patchState(store, <NonNullable<object>>{ personTax, ...setPropLoaded('submitPersonTax') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...error.error.Errors, ...setPropError('submitPersonTax', error) })
                        }
                    })
                ))
        ))
    }),
    ),
    withReqState('getPersonTax'),
    withReqState('submitPersonTax')
)
const configPersonGroup = entityConfig(
    {
      entity: type<IPersonGroup>(),
      collection: 'personGroup',
      selectId: (group: IPersonGroup) => group.GroupId
    },
)
export const GroupStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withEntities(configPersonGroup),
    withMethods((
        store,
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore)
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonGroup', 'submitPersonGroup', 'deletePersonGroup')});
        },
        loadPersonGroup: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('getPersonGroup') })),
            switchMap((payload) => personService.getPersonGroup(payload)
                .pipe(
                    tapResponse({
                        next: (personGroup) => {
                            const group = personGroup ?? [];
                            patchState(store, addEntities(group, configPersonGroup), setPropLoaded('getPersonGroup'))
                        },
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('getPersonGroup', error) })
                        }
                    })
                ))
        )),
        createPersonGroup: rxMethod<IPersonGroup>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonGroup') })),
            switchMap((payload) => personService.createPersonGroup(payload)
                .pipe(
                    tapResponse({
                        next: (personGroup) => {
                            patchState(store, addEntity(personGroup, configPersonGroup), setPropLoaded('submitPersonGroup'));
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonGroup', error) })
                        }
                    })
                ))
        )),

        updatePersonGroup: rxMethod<IPersonGroup>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonGroup') })),
            switchMap((payload) => personService.updatePersonGroup(payload)
                .pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, updateEntity({ id: payload.GroupId, changes: payload }, configPersonGroup), setPropLoaded('submitPersonGroup'))
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonGroup', error) })
                        }
                    })
                ))
        )),
        deletePersonGroup: rxMethod<string>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('deletePersonGroup') })),
            switchMap((payload) => personService.deletePersonGroup(payload)
                .pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, removeEntity(payload, { collection: 'personGroup' }), setPropLoaded('deletePersonGroup'));
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('deletePersonGroup', error) })
                        }
                    })
                ))
        ))
    }),
    ),
    withReqState('getPersonGroup'),
    withReqState('submitPersonGroup'),
    withReqState('deletePersonGroup')
)

export const IntegrationStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        personService = inject(PersonService),
        alertStore = inject(AlertStore),
        errorStore = inject(ErrorStore)
    ) => ({
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPersonIntegration', 'submitPersonIntegration')});
        },
        loadPersonIntegration: rxMethod<string>(pipe(
            tap(() => patchState(store, { personIntegration: null, ...setPropLoading('getPersonIntegration') })),
            switchMap((payload) => personService.getPersonIntegration(payload)
                .pipe(
                    tapResponse({
                        next: (personIntegration) => {
                            patchState(store, { personIntegration, ...setPropLoaded('getPersonIntegration') })
                        },
                        error: (error: HttpErrorResponse) => {
                            console.error(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('getPersonIntegration', error) })
                        }
                    })
                )
            )
        )),
        createPersonIntegration: rxMethod<IPersonIntegration>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonIntegration') })),
            switchMap((payload) => personService.createPersonIntegration(payload)
                .pipe(
                    tapResponse({
                        next: (personIntegration) => {
                            patchState(store, { personIntegration, ...setPropLoaded('submitPersonIntegration') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonIntegration', error) })
                        }
                    })
                )
            )
        )),
        updatePersonIntegration: rxMethod<IPersonIntegration>(pipe(
            tap(() => patchState(store, <NonNullable<object>>{ ...setPropLoading('submitPersonIntegration') })),
            switchMap((payload) => personService.updatePersonIntegration(payload)
                .pipe(
                    tapResponse({
                        next: (personIntegration) => {
                            patchState(store, { personIntegration, ...setPropLoaded('submitPersonIntegration') });
                            alertStore.genericSuccess();
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, <NonNullable<object>>{ ...setPropError('submitPersonIntegration', error) })
                        }
                    })
                )
            )
        )),
    }),
    ),
    withReqState('getPersonIntegration', 'submitPersonIntegration')
)

export const PersonStore = signalStore(
    { providedIn: 'root' },
    withState<IPersonStore>(initialPersonStore),
    withMethods((
        store,
        errorStore = inject(ErrorStore),
        alertStore = inject(AlertStore),
        personService = inject(PersonService)
    ) => ({
        clearPersonData: () => patchState(store, initialPersonStore),
        blankRequestState: () => {
            patchState(store, { ...setPropInit('getPerson', 'submitPerson', 'deletePerson', 'createPerson', 'updatePerson')});
        },
        loadPersonWithUserData: rxMethod<string>(pipe(
            tap((currentPersonId) => patchState(store, { currentPersonId, ...setPropLoading('getPerson') })),
            switchMap((payload) => personService.getPersonWithUserData(payload)
                .pipe(
                    tapResponse({
                        next: (personData) => {
                            patchState(store, { personData, ...setPropLoaded('getPerson') })
                        },
                        error: (loadPersonError: HttpErrorResponse) => {
                            console.error(loadPersonError);
                            patchState(store, { loadPersonError, ...setPropError('getPerson', loadPersonError) });
                        }
                    })
                ))
        )),
        loadPerson: rxMethod<string>(pipe(
            tap((currentPersonId) => patchState(store, { currentPersonId, ...setPropLoading('getPerson') })),
            switchMap((payload) => personService.getPerson(payload)
                .pipe(
                    tapResponse({
                        next: (personData) => patchState(store, { personData, ...setPropLoaded('getPerson') }),
                        error: (error) => patchState(store, { ...setPropError('getPerson', error) }),
                    })
                ))
        )),
        createPerson: rxMethod<Partial<IPerson>>(pipe(
            tap(() => patchState(store, { ...setPropLoading('createPerson') })),
            switchMap((payload) => personService.createPerson(payload)
                .pipe(
                    tapResponse({
                        next: (personData) => {
                            patchState(store, { currentPersonId: personData.PersonId, personData, ...setPropLoaded('createPerson') });
                            alertStore.create({ message: 'PERSON.GENERAL.ADD_SUCCESSFULLY', type: 'success',  title: 'ALERT.TITLE_SUCCESS' });
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, { ...setPropError('createPerson', error) });
                        }
                    })
                ))
        )),
        updatePerson: rxMethod<Partial<IPerson>>(pipe(
            tap(() => patchState(store, { ...setPropLoading('updatePerson') })),
            switchMap((payload) => personService.updatePerson(payload)
                .pipe(
                    tapResponse({
                        next: (personData) => {
                            patchState(store, { personData, ...setPropLoaded('updatePerson') });
                            alertStore.create({ message: 'PERSON.GENERAL.UPDATE_SUCCESSFULLY', type: 'success',  title: 'ALERT.TITLE_SUCCESS' });
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.setHttpError(error);
                            patchState(store, { ...setPropError('updatePerson', error) });
                        }
                    })
                ))
        )),
    })
    ),
    withReqState('getPerson', 'submitPerson', 'deletePerson', 'createPerson', 'updatePerson'),
);
